import { useEffect, useRef } from 'react';
import { useSystemSettings } from '../context/SystemSettingsContext';

// ETL 상태를 가져오는 함수 (AI 서버)
const AI_SERVER_URL = process.env.REACT_APP_AI_SERVER_URL || 'http://localhost:8001';

async function fetchEtlStatus() {
    try {
        const response = await fetch(`${AI_SERVER_URL}/api/etl/status`);
        if (!response.ok) return null;
        return await response.json();
    } catch (error) {
        console.error('ETL status fetch failed:', error);
        return null;
    }
}

/**
 * 시스템 설정의 refreshInterval에 따라 자동 새로고침을 수행하는 커스텀 훅
 * @param {Function} callback - 새로고침 시 호출할 함수
 * @param {boolean} enabled - 활성화 여부 (기본값: true)
 */
export function useAutoRefresh(callback, enabled = true) {
    const { settings } = useSystemSettings();
    const savedCallback = useRef();
    const lastEtlSuccessRef = useRef(null);

    // callback이 변경될 때마다 ref 업데이트
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // 일반 interval 모드
    useEffect(() => {
        if (!enabled) return;

        const refreshInterval = settings?.refreshInterval;

        // '수집완료시' 모드는 별도 useEffect에서 처리
        if (refreshInterval === '수집완료시') return;

        const intervalMs = parseRefreshInterval(refreshInterval);

        // '사용안함'인 경우 interval 설정하지 않음
        if (intervalMs === null) return;

        const tick = () => {
            if (savedCallback.current) {
                savedCallback.current();
            }
        };

        const id = setInterval(tick, intervalMs);
        return () => clearInterval(id);
    }, [settings?.refreshInterval, enabled]);

    // ETL 폴링 모드 ('수집완료시')
    // 실제 새 데이터(records_processed > 0)가 INSERT 됐을 때만 새로고침
    useEffect(() => {
        if (!enabled) return;
        if (settings?.refreshInterval !== '수집완료시') return;

        // 5초마다 ETL 상태 확인 (ETL 경량 주기와 동일)
        const pollInterval = setInterval(async () => {
            const status = await fetchEtlStatus();
            if (!status || !status.last_success) return;

            const currentSuccess = status.last_success;

            // 처음 폴링 시 초기화
            if (lastEtlSuccessRef.current === null) {
                lastEtlSuccessRef.current = currentSuccess;
                return;
            }

            // ETL 완료 시간이 변경되었고, 실제 새 데이터가 있을 때만 새로고침
            if (currentSuccess !== lastEtlSuccessRef.current) {
                lastEtlSuccessRef.current = currentSuccess;

                // 새로 처리된 레코드가 있을 때만 갱신
                if (status.records_processed > 0 && savedCallback.current) {
                    // ETL 완료 후 DB 커밋까지 약간의 딜레이
                    setTimeout(() => {
                        savedCallback.current();
                    }, 1000);
                }
            }
        }, 5000);

        return () => clearInterval(pollInterval);
    }, [settings?.refreshInterval, enabled]);
}

/**
 * refreshInterval 문자열을 밀리초로 변환
 * @param {string} interval - '수집완료시', '10초', '30초', '1분', '5분', '10분', '사용안함'
 * @returns {number|null|string} - 밀리초, null(사용안함), 또는 'etl'(수집완료시)
 */
function parseRefreshInterval(interval) {
    switch (interval) {
        case '수집완료시':
            return 'etl';
        case '10초':
            return 10000;
        case '30초':
            return 30000;
        case '1분':
            return 60000;
        case '5분':
            return 300000;
        case '10분':
            return 600000;
        case '사용안함':
            return null;
        default:
            return 30000; // 기본값 30초
    }
}

export default useAutoRefresh;
